import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Trophy, X, Share2, Copy, ImageDown } from "lucide-react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { formatMoney } from "@/lib/currency";
import NumberRollup from "@/components/NumberRollup";
import ConfettiBurst from "@/components/ConfettiBurst";
import GoalShareCard from "@/components/goal/GoalShareCard";
import { describeGoalTiming } from "@/components/goal/describeGoalTiming";

/**
 * GoalCompleteModal — full-screen celebration when a goal hits 100 %.
 *
 * Props:
 *   goal     — the goal object ({ id, name, target_amount, deadline, saved_amount, ... })
 *   currency — user currency code
 *   onClose  — called when user dismisses the modal
 *
 * Note: The off-screen ShareCard remains in English on purpose — it's
 * exported as a PNG for social platforms (WhatsApp / Instagram) where
 * English copy travels best and doesn't require RTL fonts inside the
 * html-to-image renderer.
 */
export default function GoalCompleteModal({ goal, currency, onClose }) {
  const { t } = useTranslation();
  const shareCardRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  // Escape key closes the modal
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!goal) return null;

  const target = Number(goal.target_amount) || 0;
  const daysAheadText = describeGoalTiming(goal, t);

  const shareText = t("goal_complete_modal.share_text", { name: goal.name, amount: formatMoney(target, currency) });

  // Render the off-screen share card into a PNG data-URL.
  const generatePng = async () => {
    if (!shareCardRef.current) return null;
    setExporting(true);
    try {
      const dataUrl = await toPng(shareCardRef.current, {
        cacheBust: true, pixelRatio: 2,
        backgroundColor: "#1A2E22",
        skipFonts: true,
      });
      return dataUrl;
    } catch (e) {
      console.error("Goal share PNG failed:", e);
      toast.error(t("goal_complete_modal.toast_create_image_failed"));
      return null;
    } finally { setExporting(false); }
  };

  const downloadPng = async () => {
    const url = await generatePng();
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    const safeName = (goal.name || "goal").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
    a.download = `goal-complete-${safeName}.png`;
    a.click();
    toast.success(t("goal_complete_modal.toast_image_saved"));
  };

  // Share the image itself when the browser supports files-share API,
  // otherwise fall back to share-text + auto-download.
  const shareImage = async () => {
    const url = await generatePng();
    if (!url) return;
    try {
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], `goal-${(goal.name || "goal").toLowerCase()}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ title: t("goal_complete_modal.eyebrow"), text: shareText, files: [file] });
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
      console.warn("File share unsupported, falling back to download", e);
    }
    // Fallback — download and copy text
    const a = document.createElement("a");
    a.href = url;
    a.download = `goal-complete.png`;
    a.click();
    try { await navigator.clipboard.writeText(shareText); } catch { /* ignore */ }
    toast.success(t("goal_complete_modal.toast_image_saved_caption"));
  };

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: t("goal_complete_modal.eyebrow"), text: shareText });
      } else {
        await navigator.clipboard.writeText(shareText);
        toast.success(t("common.toast_copied"));
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
      try { await navigator.clipboard.writeText(shareText); toast.success(t("common.toast_copied")); }
      catch { toast.error(t("goal_complete_modal.toast_share_failed")); }
    }
  };

  const copyOnly = async () => {
    try { await navigator.clipboard.writeText(shareText); toast.success(t("common.toast_copied")); }
    catch { toast.error(t("goal_complete_modal.toast_clipboard_blocked")); }
  };

  return createPortal(
    <>
      <ConfettiBurst trigger={goal.id} />
      <div
        className="fixed inset-0 z-[9998] flex items-center justify-center p-4"
        style={{ background: "rgba(15, 20, 25, 0.72)", backdropFilter: "blur(4px)" }}
        onClick={onClose}
        data-testid="goal-complete-modal"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="relative max-w-md w-full rounded-2xl overflow-hidden shadow-lift animate-fadeup"
          style={{
            background: "linear-gradient(180deg, #FFFFFF 0%, #FBF5EB 100%)",
            border: "1px solid rgba(224,158,80,0.35)",
          }}
        >
          <button
            onClick={onClose}
            className="absolute top-3 right-3 h-8 w-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-accent)] transition-colors"
            data-testid="goal-complete-close-btn"
            aria-label={t("goal_complete_modal.close_label")}
          >
            <X size={16} />
          </button>

          <div className="p-8 pt-10 text-center">
            <div
              className="mx-auto h-16 w-16 rounded-2xl flex items-center justify-center mb-5 shadow-warm gentle-float"
              style={{
                background: "linear-gradient(135deg, #2D5A40 0%, #E09E50 100%)",
                color: "#FFFFFF",
              }}
            >
              <Trophy size={30} strokeWidth={2} />
            </div>

            <div className="label-eyebrow" style={{ color: "#A16A25" }}>{t("goal_complete_modal.eyebrow")}</div>
            <h2
              className="text-3xl md:text-4xl font-medium tracking-tight leading-tight mt-2"
              style={{ fontFamily: "var(--font-heading)" }}
              data-testid="goal-complete-title"
            >
              {t("goal_complete_modal.title")}
            </h2>
            <p className="text-[var(--text-secondary)] mt-3 leading-relaxed">
              {t("goal_complete_modal.body_prefix")}
              <span className="font-mono font-medium text-[var(--text-primary)]" data-testid="goal-complete-amount">
                <NumberRollup value={target} currency={currency} duration={1100} />
              </span>
              {t("goal_complete_modal.body_for")}
              <span className="font-medium text-[var(--text-primary)]">{goal.name}</span>.
            </p>
            {daysAheadText && (
              <p className="text-xs text-[var(--text-muted)] mt-2" data-testid="goal-complete-timing">
                {daysAheadText}
              </p>
            )}

            <div className="mt-8 flex items-center gap-2 justify-center flex-wrap">
              <button
                onClick={shareImage}
                className="btn-primary"
                disabled={exporting}
                data-testid="goal-complete-share-btn"
              >
                <Share2 size={14} />
                {exporting ? t("goal_complete_modal.preparing") : t("goal_complete_modal.share_image")}
              </button>
              <button
                onClick={downloadPng}
                className="btn-ghost"
                title={t("goal_complete_modal.save_png_title")}
                disabled={exporting}
                data-testid="goal-complete-download-btn"
              >
                <ImageDown size={14} />
              </button>
              <button
                onClick={share}
                className="btn-ghost"
                title={t("goal_complete_modal.share_text_only")}
                data-testid="goal-complete-share-text-btn"
              >
                <Share2 size={14} />
              </button>
              <button
                onClick={copyOnly}
                className="btn-ghost"
                title={t("goal_complete_modal.copy_caption")}
                data-testid="goal-complete-copy-btn"
              >
                <Copy size={14} />
              </button>
            </div>

            <button
              onClick={onClose}
              className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] mt-5"
              data-testid="goal-complete-continue-btn"
            >
              {t("goal_complete_modal.keep_momentum")}
            </button>
          </div>
        </div>
      </div>
      {/* Off-screen 1080×1080 share card — captured to PNG on demand */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          top: 0,
          left: "-99999px",
          pointerEvents: "none",
          opacity: 0,
        }}
      >
        <GoalShareCard ref={shareCardRef} goal={goal} currency={currency} />
      </div>
    </>,
    document.body,
  );
}

